import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { fromZonedTime } from "date-fns-tz";
import { EventFormData } from "@/components/admin/event-form/types";

const createEventData = (data: EventFormData, coordinates: { lat: number; lng: number } | null, hostType: string) => {
  // Special handling for non-UUID hosts (nimi, member, and museum)
  const isDefaultHost = data.hostId === 'nimi' || data.hostId === 'member' || data.hostId === 'museum';
  const tz = data.timezone || 'Europe/London';

  // Resolve early bird tri-state → columns
  let early_bird_enabled: boolean | null = null;
  let early_bird_days_before: number | null = null;
  let early_bird_discount_percent: number | null = null;
  if (data.earlyBirdMode === 'disabled') {
    early_bird_enabled = false;
  } else if (data.earlyBirdMode === 'custom') {
    early_bird_enabled = true;
    early_bird_days_before = data.earlyBirdDaysBefore != null && String(data.earlyBirdDaysBefore) !== ''
      ? parseInt(String(data.earlyBirdDaysBefore)) : null;
    early_bird_discount_percent = data.earlyBirdDiscountPercent != null && String(data.earlyBirdDiscountPercent) !== ''
      ? parseFloat(String(data.earlyBirdDiscountPercent)) : null;
  }

  return {
    title: data.title,
    description: data.description || null,
    venue: data.venue,
    latitude: coordinates?.lat || null,
    longitude: coordinates?.lng || null,
    image_url: data.imageUrl || null,
    start_time: fromZonedTime(data.startTime, tz).toISOString(),
    end_time: fromZonedTime(data.endTime, tz).toISOString(),
    capacity: parseInt(String(data.capacity)),
    price: parseFloat(String(data.price)),
    credit_cost: data.creditCost ? parseInt(String(data.creditCost)) : Math.ceil(parseFloat(String(data.price)) / 2.5),
    whatsapp_group: data.whatsappGroup || null,
    host_id: isDefaultHost ? null : data.hostId || null,
    secondary_host_id: data.secondaryHostId || null,
    host: hostType,
    agenda_text: data.agendaText || null,
    allowed_booking_methods: data.allowedBookingMethods || ['stripe', 'credits', 'unlimited'],
    required_tags: data.requiredTags?.length > 0 ? data.requiredTags : null,
    timezone: data.timezone || 'Europe/London',
    currency: (data.currency || 'gbp').toLowerCase(),
    early_bird_enabled,
    early_bird_days_before,
    early_bird_discount_percent,
    bring_friend_eligible: data.bringFriendEligible === 'enabled' ? true : data.bringFriendEligible === 'disabled' ? false : null,
  };
};

/**
 * Waves and events.capacity are saved together by the set_event_groups RPC: the sum invariant
 * is checked at commit, so changing either half in a separate call can never commit.
 */
const saveEventGroups = async (eventId: string, data: EventFormData, capacity: number) => {
  const tz = data.timezone || 'Europe/London';
  const groups = (data.groups || []).map((g) => ({
    id: g.id ?? null,
    name: g.name.trim(),
    start_time: fromZonedTime(g.startTime, tz).toISOString(),
    end_time: g.endTime ? fromZonedTime(g.endTime, tz).toISOString() : null,
    capacity: parseInt(String(g.capacity), 10) || 0,
    host_id: g.hostId || null,
    secondary_host_id: g.secondaryHostId || null,
    meeting_venue: g.meetingVenue?.trim() || null,
    whatsapp_group: g.whatsappGroup?.trim() || null,
    display_order: g.displayOrder ?? 0,
  }));
  const { error } = await supabase.rpc('set_event_groups', {
    _event_id: eventId,
    _capacity: capacity,
    _groups: groups,
  });
  if (error) throw error;
};

const insertEventTags = async (eventId: string, tags: string[]) => {
  if (tags.length === 0) return;
  
  const { error: tagsError } = await supabase
    .from('event_tags')
    .insert(
      tags.map(tag => ({
        event_id: eventId,
        tag: tag.trim()
      }))
    );

  if (tagsError) throw tagsError;
};

const updateExistingEvent = async (
  eventId: string, 
  eventData: any, 
  tags: string[]
) => {
  try {
    // Delete existing tags
    const { error: tagDeleteError } = await supabase
      .from('event_tags')
      .delete()
      .eq('event_id', eventId);

    if (tagDeleteError) throw tagDeleteError;

    // Update the event
    const { error: eventError } = await supabase
      .from('events')
      .update(eventData)
      .eq('id', eventId);

    if (eventError) throw eventError;

    // Handle tags
    await insertEventTags(eventId, tags);
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

const createNewEvent = async (eventData: any, tags: string[]) => {
  const { data: createdEvent, error: eventError } = await supabase
    .from('events')
    .insert([eventData])
    .select()
    .single();

  if (eventError) throw eventError;

  // Handle tags
  await insertEventTags(createdEvent.id, tags);

  return createdEvent;
};

export const useCreateEvent = (onClose: () => void) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const createEvent = async (
    data: EventFormData, 
    coordinates: { lat: number; lng: number } | null,
    tags: string[],
    hostType: string,
    eventId?: string,
  ) => {
    setIsSubmitting(true);
    let returnEventId: string = eventId || '';
    try {
      const eventData = createEventData(data, coordinates, hostType);
      const hasGroups = (data.groups?.length ?? 0) > 0;
      const hadGroups = (data.originalGroupIds?.length ?? 0) > 0;

      if (eventId) {
        // Fetch current capacity before update to detect increases
        const { data: oldEvent } = await supabase
          .from('events')
          .select('capacity')
          .eq('id', eventId)
          .single();

        if (hasGroups || hadGroups) {
          // Waves first (with capacity, atomically) so a refused wave edit saves nothing.
          await saveEventGroups(eventId, data, eventData.capacity);
          const { capacity: _capacity, ...rest } = eventData;
          await updateExistingEvent(eventId, rest, tags);
        } else {
          await updateExistingEvent(eventId, eventData, tags);
        }
        returnEventId = eventId;

        // If capacity increased, notify interested users (fire-and-forget)
        if (oldEvent && eventData.capacity > oldEvent.capacity) {
          supabase.functions.invoke('notify-event-interest', {
            body: { eventId }
          }).catch(err => console.error('Failed to notify interested users:', err));
        }
      } else {
        const newEvent = await createNewEvent(eventData, tags);
        returnEventId = newEvent.id;
        if (hasGroups) await saveEventGroups(newEvent.id, data, eventData.capacity);
      }


      toast({
        title: "Success",
        description: `Event ${eventId ? 'updated' : 'created'} successfully`,
      });
      onClose();
    } catch (error: any) {
      console.error('Error creating/updating event:', error);
      toast({
        title: "Error",
        description: error?.message
          ? `Failed to ${eventId ? 'update' : 'create'} event: ${error.message}`
          : `Failed to ${eventId ? 'update' : 'create'} event`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }

    return returnEventId;
  };

  return { createEvent, isSubmitting };
};